/**
 * The retarget's own refusals as evidence markers (Tripo rig presets,
 * 2026-09-23): a family frame the rig contradicted (`authored-frame-refused`)
 * and a spine witness pair that disagreed across rigs
 * (`posture-witness-demoted`). Automatic is fine, silent is not.
 *
 * One marker per VERDICT, not per take: the verdict is a property of the rig
 * pair, so repeated takes from one source collapse. But the pair has two
 * rigs — a TARGET-side frame refusal belongs to the target bone alone, while
 * a SOURCE-side refusal and every posture contradiction are verdicts about a
 * specific source rig, keyed by its label, so two sources contradicting the
 * same target bone stay two markers (Codex P2, #185).
 *
 * Depends on: @harryhausen/kernel (types).
 */
import type { EvidenceMarker, RetargetReport } from '@harryhausen/kernel';

export interface RetargetMarkerInput {
  readonly sourceLabel: string;
  readonly result: { readonly report: Pick<RetargetReport, 'authoredFrameRefusals' | 'postureWitnessContradictions'> };
}

export function retargetEvidenceMarkers(clips: readonly RetargetMarkerInput[]): EvidenceMarker[] {
  const seen = new Set<string>();
  const out: EvidenceMarker[] = [];
  for (const { sourceLabel, result } of clips) {
    for (const r of result.report.authoredFrameRefusals) {
      const key = r.side === 'target' ? `frame:target:${r.bone}` : `frame:source:${sourceLabel}:${r.bone}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        code: 'authored-frame-refused',
        detail: {
          family: r.family, ontologyId: r.ontologyId, bone: r.bone, side: r.side, reason: r.reason,
          angleDeg: Math.round(r.angleDeg * 10) / 10, toleranceDeg: r.toleranceDeg, fallback: r.fallback, source: sourceLabel,
        },
      });
    }
    for (const c of result.report.postureWitnessContradictions) {
      const key = `posture:${sourceLabel}:${c.ontologyId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        code: 'posture-witness-demoted',
        detail: {
          ontologyId: c.ontologyId, sourceBone: c.sourceBone, targetBone: c.targetBone,
          separationDeg: Math.round((c.separationRadians * 1800) / Math.PI) / 10, source: sourceLabel,
        },
      });
    }
  }
  return out;
}
